import React, { useMemo } from "react";
import { useT, Namespace } from "@racoon/i18n";
import { DataTable, Column } from "@racoon/ui";
import { ConnectProto } from "@racoon/api";
import { useSelectedCompany } from "~/shared/store/company/hooks/useSelectedCompany";
import { useTableSettings } from "~/shared/hooks/useTableSettings";

type ContainerType = ConnectProto.ContainerType;

interface BunkerTypesTableProps {
  containerTypes: ContainerType[];
  isLoading: boolean;
  selectedType?: ContainerType | null;
  onRowSelect?: (type: ContainerType | null) => void;
}

export const BunkerTypesTable: React.FC<BunkerTypesTableProps> = ({
  containerTypes,
  isLoading,
  selectedType,
  onRowSelect,
}) => {
  const t = useT();
  const { selectedCompanyId } = useSelectedCompany();
  const { columnConfig, handleColumnConfigChange } = useTableSettings(
    "bunker_types",
    selectedCompanyId || undefined,
  );

  const columns = useMemo<Column<ContainerType>[]>(() => {
    return [
      {
        id: "index",
        header: "№",
        accessor: (_r, i) => i + 1,
        width: 64,
      },
      {
        id: "name",
        header: t(Namespace.Settings, "bunkerType.table.name"),
        key: "name",
        sortable: true,
        width: 300,
      },
      {
        id: "volume",
        header: t(Namespace.Settings, "bunkerType.table.volume"),
        key: "volume",
        sortable: true,
        width: 150,
        cell: (info) => `${info.getValue()} м³`,
      },
      {
        id: "weight",
        header: t(Namespace.Settings, "bunkerType.table.weight"),
        key: "weightKg",
        sortable: true,
        width: 150,
        cell: (info) => `${info.getValue()} кг`,
      },
      {
        // Types are shared across the holding: the company that made each one
        id: "owner",
        header: t(Namespace.Settings, "bunkerType.table.owner"),
        accessor: (row) => row.ownerOrganization?.name ?? "",
        width: 220,
      },
    ];
  }, [t]);

  if (containerTypes.length === 0 && !isLoading) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-8 text-center">
        <p className="text-gray-500">{t(Namespace.Settings, "bunkerType.table.empty")}</p>
      </div>
    );
  }

  return (
    <div className="min-h-0 flex-1 overflow-hidden bg-white">
      <DataTable
        className="h-full"
        columns={columns}
        data={containerTypes}
        loading={isLoading}
        rowKey={(row) => String(row.id)}
        selectedRowKeys={selectedType ? [String(selectedType.id)] : []}
        onRowClick={
          onRowSelect
            ? (row) => {
                if (selectedType?.id === row.id) {
                  onRowSelect(null);
                } else {
                  onRowSelect(row);
                }
              }
            : undefined
        }
        columnConfig={columnConfig}
        onColumnConfigChange={handleColumnConfigChange}
        namespace={Namespace.Settings}
      />
    </div>
  );
};
